import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  BarService,
  FrontDeskService,
  HousekeepingService,
  KioskService,
  LocationsService,
  RoomsService,
  RoomStatusService,
  StaffService,
} from '@rocksoft/locations-sdk';
import { AddCategoryDto, CategoryDto, CategoryService } from '@rocksoft/settings-sdk';
import { CategoryType, ErrorModel } from '@rocksoft/shared';
import { compare } from 'fast-json-patch';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import {
  CategoryApiActions,
  CategoryGeoLocationsActions,
  CategoryListActions,
  CategoryLoadActions,
  CategoryResolverActions,
  CategoryStatusContainerActions,
  LocationsSignalRHubActions,
} from './category.actions';
import { selectCategoryRequestStatuses } from './category.selector';
import { CategoriesUnion, categoryEntityFrom, categoryRequestKey } from './category.state';

const assignableCategoryTypes: CategoryType[] = ['Room', 'Housekeeping', 'Staff', 'FrontDesk', 'Bar', 'Kiosk'];

@Injectable()
export class CategoryEffects {
  private readonly actions$ = inject(Actions);
  private readonly barService = inject(BarService);
  private readonly frontDeskService = inject(FrontDeskService);
  private readonly housekeepingService = inject(HousekeepingService);
  private readonly kioskService = inject(KioskService);
  private readonly locationService = inject(LocationsService);
  private readonly categoryService = inject(CategoryService);
  private readonly roomsService = inject(RoomsService);
  private readonly roomStatusService = inject(RoomStatusService);
  private readonly staffService = inject(StaffService);
  private readonly store = inject(Store);

  assignableChildrenRequestedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryLoadActions.assignableChildrenRequested),
      concatLatestFrom(() => this.store.select(selectCategoryRequestStatuses)),
      mergeMap(([{ locationCategoryId }, requestStatuses]) =>
        assignableCategoryTypes
          .filter((categoryType) => !requestStatuses[categoryRequestKey(categoryType, locationCategoryId)])
          .map((categoryType) =>
            CategoryResolverActions.categoriesRequested({ categoryType, parentCategoryId: locationCategoryId })
          )
      )
    );
  });

  unavailableCategoryRequestedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryLoadActions.unavailableCategoryRequested),
      concatLatestFrom(() => this.store.select(selectCategoryRequestStatuses)),
      mergeMap(([{ brandCategoryId }, requestStatuses]) => {
        const requestKey = categoryRequestKey('Unavailable', brandCategoryId);
        return requestStatuses[requestKey]
          ? EMPTY
          : of(
              CategoryResolverActions.categoriesRequested({
                categoryType: 'Unavailable',
                parentCategoryId: brandCategoryId,
              })
            );
      })
    );
  });

  categoriesLoadedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryResolverActions.categoriesRequested),
      mergeMap((action) =>
        safeMap(
          this.loadCategories(action.categoryType, action.parentCategoryId),
          (configurations) =>
            CategoryApiActions.categoriesLoaded({
              configurations,
              categoryType: action.categoryType,
              parentCategoryId: action.parentCategoryId,
            }),
          (error) =>
            CategoryApiActions.categoriesLoadedError({
              error,
              categoryType: action.categoryType,
              parentCategoryId: action.parentCategoryId,
            })
        )
      )
    );
  });

  categoryAddedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryListActions.categoryAdded),
      mergeMap((action) =>
        safeMap(
          this.addCategory(action.entity),
          (configuration) => CategoryApiActions.categoryAdded({ configuration }),
          (error) => CategoryApiActions.categoryAddedError({ configuration: action.entity, error })
        )
      )
    );
  });

  categoryUpdatedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryListActions.categoryUpdated),
      mergeMap((action) =>
        safeMap(
          this.updateCategoryIdentity(action.originalEntity, action.entity),
          (syncedViaApi) =>
            // No-op rename: clear processing locally. Real rename: wait for SignalR.
            syncedViaApi ? [] : CategoryApiActions.categoryUpdated({ configuration: action.entity }),
          (error) =>
            CategoryApiActions.categoryUpdatedError({
              originalConfiguration: action.originalEntity,
              configuration: action.entity,
              error,
            }),
          'The category display name could not be updated.'
        )
      )
    );
  });

  categoryDeletedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryListActions.categoryDeleted),
      mergeMap((action) =>
        this.deleteCategory(action.entity).pipe(
          // Return EMPTY on success - let SignalR OnCategoryDeleted remove from store
          mergeMap(() => EMPTY),
          catchError((error) =>
            of(CategoryApiActions.categoryDeletedError({ configuration: action.entity, error: error as ErrorModel }))
          )
        )
      )
    );
  });

  categoryGeoLocationsUpdatedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryGeoLocationsActions.categoryUpdated),
      mergeMap((action) =>
        safeMap(
          this.updateCategoryGeo(action.originalEntity, action.entity),
          (configuration) => CategoryApiActions.categoryUpdated({ configuration }),
          (error) =>
            CategoryApiActions.categoryUpdatedError({
              originalConfiguration: action.originalEntity,
              configuration: action.entity,
              error,
            })
        )
      )
    );
  });

  categoryStatusChangedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategoryStatusContainerActions.statusChanged),
      mergeMap((action) => {
        const serviceCall = action.status
          ? this.roomStatusService.checkInById({
              roomCategoryId: action.room.categoryId,
              checkInCommand: action.room.reservation,
            })
          : this.roomStatusService.checkOutById({
              roomCategoryId: action.room.categoryId,
            });

        return safeMap(
          serviceCall,
          () =>
            CategoryApiActions.statusChanged({
              categoryType: 'Room',
              categoryId: action.room.categoryId,
              status: action.status,
            }),
          (error) =>
            CategoryApiActions.statusChangedError({ categoryType: 'Room', categoryId: action.room.categoryId, error })
        );
      })
    );
  });

  locationsSignalRLocationUpdatedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationsSignalRHubActions.locationUpdated),
      mergeMap((action) => {
        if (!action.location) {
          return EMPTY;
        }

        return of(
          CategoryApiActions.categoryUpdated({
            configuration: categoryEntityFrom('Location', action.location),
          })
        );
      })
    );
  });

  locationsSignalRRoomUpdatedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationsSignalRHubActions.roomUpdated),
      mergeMap((action) => {
        if (!action.room) {
          return EMPTY;
        }

        return of(
          CategoryApiActions.categoryUpdated({
            configuration: categoryEntityFrom('Room', action.room),
          })
        );
      })
    );
  });

  locationsSignalRLocationDeletedEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationsSignalRHubActions.categoryDeleted),
      mergeMap((action) => {
        if (!action.categoryId) {
          return EMPTY;
        }

        return of(
          CategoryApiActions.categoryDeleted({
            configuration: {
              categoryType: action.categoryType,
              categoryId: action.categoryId,
            } as CategoriesUnion,
          })
        );
      })
    );
  });

  private loadCategories(categoryType: CategoryType, parentCategoryId?: string): Observable<CategoriesUnion[]> {
    switch (categoryType) {
      case 'Location':
        return this.locationService
          .getLocations()
          .pipe(map((locations) => locations.map((location) => categoryEntityFrom('Location', location))));
      case 'Room':
        if (!parentCategoryId) {
          return EMPTY;
        }
        return this.roomsService
          .getRooms({ locationCategoryId: parentCategoryId })
          .pipe(map((rooms) => rooms.map((room) => categoryEntityFrom('Room', room))));
      case 'Bar':
        if (!parentCategoryId) return EMPTY;
        return this.barService
          .getBar({ locationCategoryId: parentCategoryId })
          .pipe(map((bar) => [categoryEntityFrom('Bar', bar)]));
      case 'FrontDesk':
        if (!parentCategoryId) return EMPTY;
        return this.frontDeskService
          .getFrontDesk({ locationCategoryId: parentCategoryId })
          .pipe(map((frontDesk) => [categoryEntityFrom('FrontDesk', frontDesk)]));
      case 'Housekeeping':
        if (!parentCategoryId) return EMPTY;
        return this.housekeepingService
          .getHousekeeping({ locationCategoryId: parentCategoryId })
          .pipe(map((housekeeping) => [categoryEntityFrom('Housekeeping', housekeeping)]));
      case 'Kiosk':
        if (!parentCategoryId) return EMPTY;
        return this.kioskService
          .getKiosk({ locationCategoryId: parentCategoryId })
          .pipe(map((kiosk) => [categoryEntityFrom('Kiosk', kiosk)]));
      case 'Staff':
        if (!parentCategoryId) return EMPTY;
        return this.staffService
          .getStaff({ locationCategoryId: parentCategoryId })
          .pipe(map((staff) => [categoryEntityFrom('Staff', staff)]));
      case 'Brand':
        return of([
          categoryEntityFrom('Brand', {
            id: 'default-brand',
            displayName: 'Default Brand',
            uniqueId: 'default-brand',
            categoryId: 'default-brand',
          }),
        ]);
      case 'Unavailable':
        if (!parentCategoryId) return EMPTY;
        return this.categoryService.getCategoryChildren({ categoryId: parentCategoryId }).pipe(
          map((categories) =>
            categories
              .filter((category) => category.categoryType === 'Unavailable')
              .map((category) =>
                categoryEntityFrom('Unavailable', {
                  categoryId: category.id,
                  humanId: category.displayName ?? category.id,
                  displayName: category.displayName ?? 'Unavailable',
                })
              )
          )
        );
      default:
        return EMPTY;
    }
  }

  private addCategory(entity: CategoriesUnion): Observable<CategoriesUnion> {
    if (entity.entity == null) {
      return throwError(() => new Error('Entity data is required to add a category'));
    }
    const addCategoryDto = {
      displayName: entity.entity.displayName,
      categoryType: entity.categoryType,
      parentCategoryId: entity.parent?.categoryId,
    } satisfies AddCategoryDto;
    return this.categoryService
      .addCategory({ addCategoryDto })
      .pipe(map((created: CategoryDto) => entity.withUpdatedIds(created.id, entity.categoryHumanId || created.id)));
  }

  /** @returns true when DCS was called; false when there was nothing to patch */
  private updateCategoryIdentity(originalEntity: CategoriesUnion, entity: CategoriesUnion): Observable<boolean> {
    if (!originalEntity.entity || !entity.entity) return EMPTY;
    const originalCategoryIdentity = {
      displayName: originalEntity.entity.displayName,
      categoryType: originalEntity.categoryType,
      parentCategoryId: originalEntity.parent?.categoryId,
    };
    const updatedCategoryIdentity = {
      displayName: entity.entity.displayName,
      categoryType: entity.categoryType,
      parentCategoryId: entity.parent?.categoryId,
    };
    const operation = compare(originalCategoryIdentity, updatedCategoryIdentity);
    if (operation.length === 0) {
      return of(false);
    }
    return this.categoryService
      .updateCategory({
        categoryId: originalEntity.entity.categoryId,
        operation,
      })
      .pipe(map(() => true));
  }

  private updateCategoryGeo(originalEntity: CategoriesUnion, entity: CategoriesUnion): Observable<CategoriesUnion> {
    switch (entity.categoryType) {
      case 'Location': {
        const originalLocation = originalEntity.entity;
        const location = entity.entity;
        if (!originalLocation || !location) return EMPTY;
        const update = compare(originalLocation, location);
        return this.locationService
          .updateLocation({ locationCategoryId: originalLocation.categoryId, operation: update })
          .pipe(map(() => categoryEntityFrom('Location', location)));
      }
      default:
        return EMPTY;
    }
  }

  private deleteCategory(entity: CategoriesUnion): Observable<CategoriesUnion> {
    switch (entity.categoryType) {
      case 'Location':
      case 'Room': {
        if (!entity.entity?.categoryId) return EMPTY;
        return this.categoryService.deleteCategory({ categoryId: entity.entity.categoryId }).pipe(map(() => entity));
      }
      default:
        return EMPTY;
    }
  }
}
